import API from "api"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ROUTES } from "routes"
import { mapDispatchProps, mapStateToProps } from "store/store"
import ReactPlayer from "react-player/vimeo"
import { debounce } from "helpers/debounce"
import { isGlovv } from "constants/domains/domains"

const LectureChapterIndex: React.FC = (props: any) => {
  const { lectureId, lessonId, chapterId } = useParams<{ lectureId: string; lessonId: string; chapterId: string }>()

  const location = useLocation()
  const navigate = useNavigate()

  const lecture = location.state?.lecture || null

  const lessons = location.state?.lessons || []
  const lesson = lessons.find((lesson: any) => lesson.id === Number(lessonId))

  const [chapters, setChapters] = useState([])
  const [fetchedLessons, setFetchedLessons] = useState(null)
  const [currentChapter, setCurrentChapter] = useState(null)

  const [duration, setDuration] = useState(0)
  const [lastProgressUpdate, setLastProgressUpdate] = useState(0)

  useEffect(() => {
    checkEnterPassword()
  }, [])

  useEffect(() => {
    fetchChapters()
    fetchLessons()
    window.scrollTo(0, 0)
  }, [chapterId, lessonId, lectureId])

  const checkEnterPassword = async () => {
    try {
      if (
        !props.preferences?.lectureUser ||
        !props.preferences?.lectureUser?.mobile ||
        !props.preferences?.lectureUser?.password
      ) {
        handleGoBack()
        return false
      }

      const response = await API.Lecture.password({
        domain: isGlovv() ? 'glovv' : 'oneulook',
        password: props.preferences?.lectureUser?.password
      })
      if (response.code == 200) {
        return true
      }
    } catch (error) {
      handleGoBack()
    }
  }

  const fetchChapters = async () => {
    try {
      const response = await API.LectureChapter.index(Number(lectureId), Number(lessonId), {
        mobile: props.preferences?.lectureUser?.mobile,
        name: props.preferences?.lectureUser?.name,
      })
      setChapters(response.data.chapters)
      const chapter = response.data.chapters.find((chapter: any) => chapter.id === Number(chapterId))
      setCurrentChapter(chapter)
    } catch (error) {
      console.error("챕터 목록을 가져오는 중 오류 발생:", error)
    }
  }

  const fetchLessons = async () => {
    try {
      const response = await API.LectureLesson.index(Number(lectureId), {
        mobile: props.preferences?.lectureUser?.mobile,
        name: props.preferences?.lectureUser?.name,
      })
      setFetchedLessons(response.data.lessons)
    } catch (error) {
      console.error("레슨 목록을 가져오는 중 오류 발생:", error)
    }
  }

  const handleGoBack = () => {
    navigate(ROUTES.LECTURE_LESSON_INDEX.SHOW(lectureId), {
      replace: true,
    })
  }

  const handleChapterClick = async (chapterId) => {
    try {
      await API.Lecture.storeWatchHistory({
        brandToken: props.auth.token,
        chapter_id: chapterId,
        name: props.preferences?.lectureUser?.name,
        mobile: props.preferences?.lectureUser?.mobile,
      })

      navigate(ROUTES.LECTURE_CHAPTER_INDEX.SHOW(lectureId, lessonId, chapterId), {
        state: { lessons, lecture },
        replace: true,
      })
    } catch (error) {
      if (error.data && error.data.errors) {
        const errorMessages = Object.values(error.data.errors).flat().join("\n")
        props.showModal({ text: `강의 시청 기록 저장 중 오류 발생\n${errorMessages}` })
      } else {
        props.showModal({ text: `강의 시청 기록 저장 중 오류 발생\n${error.message}` })
      }
    }
  }

  const renderChapters = () => {
    return chapters.length > 0 ? (
      <ul className="space-y-[10px]">
        {chapters.map((chapter: any, index: number) => {
          const isSelected = chapter.id === Number(chapterId)
          const isWatching =
            chapter.histories &&
            chapter.histories.length &&
            chapter.histories[0].progress < 95 &&
            chapter.histories[0].progress > 0
          const isWatched = chapter.histories && chapter.histories.length && chapter.histories[0].progress >= 95

          return (
            <React.Fragment key={chapter.id}>
              <li
                className={`bg-white p-3 border cursor-pointer
                  ${!isSelected && isWatched ? "border-[#E8E8E8]" : ""}
                  ${!isSelected && isWatching ? "border-[#E8E8E8]" : ""}
                  ${isSelected ? "border-[#000000] border-2" : ""}`}
                onClick={() => handleChapterClick(chapter.id)}
              >
                <div className="flex items-center">
                  <div className="mr-[20px]">
                    {true ? (
                      <img src={"/assets/icons/ic-video-play-active.svg"} />
                    ) : (
                      <img src={"/assets/icons/ic-video-play-inactive.svg"} />
                    )}
                  </div>

                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center space-x-[10px]">
                      <span
                        className={`text-[0.9375rem] font-bold 
                          ${!isSelected && isWatched ? "text-[#999999]" : ""}
                          ${!isSelected && isSelected ? "text-[#000000]" : ""}`}
                      >
                        {`${chapter.order}. ${chapter.title}`}
                      </span>
                      {isWatched ? (
                        <div
                          className={`flex items-center border rounded-[4px] px-[2px] text-[13px] font-semibold border-[#E8E8E8] text-[#999999]`}
                        >
                          <span>시청완료</span>
                        </div>
                      ) : null}
                      {isWatching ? (
                        <div
                          className={`flex items-center border rounded-[4px] px-[2px] text-[13px] font-semibold border-[#000000] text-[#000000]`}
                        >
                          <span>시청중</span>
                        </div>
                      ) : null}
                    </div>
                    {chapter.duration && (
                      <span className="ml-auto text-[#999999] text-[0.9375rem]">
                        {new Date(chapter.duration * 1000).toISOString().substr(11, 8)}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            </React.Fragment>
          )
        })}
      </ul>
    ) : null
  }

  const handleLessonClick = async (lessonId, chapterId) => {
    try {
      await API.Lecture.storeWatchHistory({
        brandToken: props.auth.token,
        lesson_id: lessonId,
        name: props.preferences?.lectureUser?.name,
        mobile: props.preferences?.lectureUser?.mobile,
      })

      navigate(ROUTES.LECTURE_CHAPTER_INDEX.SHOW(lectureId, lessonId, chapterId), {
        state: { lessons, lecture },
        replace: true,
      })
    } catch (error) {
      if (error.data && error.data.errors) {
        const errorMessages = Object.values(error.data.errors).flat().join("\n")
        props.showModal({ text: `강의 시청 기록 저장 중 오류 발생\n${errorMessages}` })
      } else {
        props.showModal({ text: `강의 시청 기록 저장 중 오류 발생\n${error.message}` })
      }
    }
  }

  const renderLessons = () => {
    return fetchedLessons && fetchedLessons.length > 0 ? (
      <ul className="space-y-2">
        <hr className="my-2 border-gray-300" />
        {fetchedLessons.map((lesson: any, index: number) => {
          const isWatching =
            lesson.histories &&
            lesson.histories.length &&
            lesson.histories[0].progress < 95 &&
            lesson.histories[0].progress > 0
          const isWatched = lesson.histories && lesson.histories.length && lesson.histories[0].progress >= 95
          const isSelected = lesson.id === Number(lessonId)

          return (
            <React.Fragment key={lesson.id}>
              <li
                className="bg-white p-3 cursor-pointer"
                onClick={() => {
                  if (!lesson.isReleased) return false
                  handleLessonClick(lesson.id, lesson.firstChapter.id)
                }}
              >
                <div className="flex items-center">
                  <div className="mr-[20px]">
                    {isWatched ? (
                      <img src={"/assets/icons/ic-video-play-inactive.svg"} />
                    ) : (
                      <img src={"/assets/icons/ic-video-play-active.svg"} />
                    )}
                  </div>

                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center space-x-[10px]">
                      <span className={`text-[1.375rem] font-bold ${isSelected ? "text-[#000000]" : ""}`}>
                        {`${lesson.order}. ${lesson.title}`}
                      </span>
                      {isWatched ? (
                        <div
                          className={`flex items-center border rounded-[4px] px-[2px] text-[13px] font-semibold border-[#E8E8E8] text-[#999999]`}
                        >
                          <span>시청완료</span>
                        </div>
                      ) : null}
                      {isWatching ? (
                        <div
                          className={`flex items-center border rounded-[4px] px-[2px] text-[13px] font-semibold border-[#000000] text-[#000000]`}
                        >
                          <span>시청중</span>
                        </div>
                      ) : null}
                    </div>
                    {lesson.duration && (
                      <span className="ml-auto text-[#999999] text-[0.9375rem]">
                        {new Date(lesson.duration * 1000).toISOString().substr(11, 8)}
                      </span>
                    )}
                  </div>
                </div>
              </li>

              {index < lessons.length - 1 && <hr className="my-2 border-gray-300" />}
            </React.Fragment>
          )
        })}
        <hr className="my-2 border-gray-300" />
      </ul>
    ) : (
      <></>
    )
  }

  const handlePreviousChapter = () => {
    const currentIndex = chapters.findIndex((chapter) => chapter.id === Number(chapterId))
    if (currentIndex > 0) {
      const previousChapterId = chapters[currentIndex - 1].id
      handleChapterClick(previousChapterId)
    } else if (currentIndex === 0 && Number(lessonId) > 1) {
      const previousLessonId =
        fetchedLessons[fetchedLessons.findIndex((lesson) => lesson.id === Number(lessonId)) - 1].id
      handleLessonClick(
        previousLessonId,
        fetchedLessons[fetchedLessons.findIndex((lesson) => lesson.id === Number(lessonId)) - 1].firstChapter.id
      )
    } else {
      props.showModal({ text: "이전 강의가 없습니다." })
    }
  }

  const handleNextChapter = () => {
    const currentIndex = chapters.findIndex((chapter) => chapter.id === Number(chapterId))
    if (currentIndex < chapters.length - 1) {
      const nextChapterId = chapters[currentIndex + 1].id
      handleChapterClick(nextChapterId)
    } else if (currentIndex === chapters.length - 1 && Number(lessonId) < fetchedLessons.length) {
      const nextLessonId = fetchedLessons[fetchedLessons.findIndex((lesson) => lesson.id === Number(lessonId)) + 1].id
      handleLessonClick(
        nextLessonId,
        fetchedLessons[fetchedLessons.findIndex((lesson) => lesson.id === Number(lessonId)) + 1].firstChapter.id
      )
    } else {
      props.showModal({ text: "다음 강의가 없습니다." })
    }
  }

  if (!lecture || !lessons) return <></>

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        {/* Go Back */}
        <button onClick={handleGoBack} className="mb-4 flex items-center space-x-2 cursor-pointer">
          <img src={"/assets/icons/ic-arrow-go-back-gray.svg"} />
          <span className="text-[0.9375rem] text-[#999999] hover:text-black hover:font-bold">뒤로가기</span>
        </button>

        {/* Lesson */}
        <div className="flex items-center">
          <h1 className="text-[1.375rem] font-bold mb-[1.25rem]">{`${lesson.order}. ${lesson.title}`}</h1>
        </div>

        {/* Video */}
        {currentChapter && (
          <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
            <ReactPlayer
              title={currentChapter.title}
              url={`${currentChapter.url}`}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              style={{ position: "absolute", top: "0", left: "0", width: "100%", height: "100%" }}
              width="100%"
              height="100%"
              controls={true}
              playing={true}
              onDuration={(duration) => {
                setDuration(duration)
              }}
              onProgress={(progress) => {
                if (duration && progress && progress.playedSeconds) {
                  const currentTime = Math.floor(progress.playedSeconds)
                  if (currentTime - lastProgressUpdate >= 10) {
                    setLastProgressUpdate(currentTime)
                    API.Lecture.storeWatchHistory({
                      brandToken: props.auth.token,
                      chapter_id: currentChapter.id,
                      name: props.preferences?.lectureUser?.name,
                      mobile: props.preferences?.lectureUser?.mobile,
                      progress: (progress.playedSeconds / duration) * 100,
                    })
                  }
                }
              }}
              onEnded={() => {
                API.Lecture.storeWatchHistory({
                  brandToken: props.auth.token,
                  chapter_id: currentChapter.id,
                  name: props.preferences?.lectureUser?.name,
                  mobile: props.preferences?.lectureUser?.mobile,
                  progress: 100,
                })
              }}
            />
          </div>
        )}

        {/* Previous and Next Chapter Buttons */}
        <div className="flex justify-center mt-4 space-x-[30px] mt-[20px] mb-[50px] items-center">
          <div
            onClick={handlePreviousChapter}
            className="flex items-center space-x-2 text-[#999999] px-4 py-2 rounded cursor-pointer"
          >
            <img src={"/assets/icons/ic-arrow-video-previous-gray.svg"} />
            <span className="text-[0.9375rem] font-bold text-[#999999] hover:text-black hover:font-bold">
              이전 강의
            </span>
          </div>

          <div className="h-[20px] w-px bg-[#D9D9D9]" />

          <div
            onClick={handleNextChapter}
            className="flex items-center space-x-2 text-[#999999] px-4 py-2 rounded cursor-pointer"
          >
            <span className="text-[0.9375rem] font-bold text-[#999999] hover:text-black hover:font-bold">
              다음 강의
            </span>
            <img src={"/assets/icons/ic-arrow-video-next-gray.svg"} />
          </div>
        </div>

        {/* Chapters */}
        {renderChapters()}

        {/* Spacer */}
        <div className="h-[100px]" />

        {/* Lessons */}
        <h1 className="text-[1.375rem] font-bold mb-[1.25rem]">전체보기</h1>
        {renderLessons()}
      </div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(LectureChapterIndex)
